import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";

const fmt$ = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * v414.68 — Bulk Sale Allocation.
 * One lump-sum sale allocated across several items from a distributor's
 * inventory. Auto-splits the total proportionally to each line's retail
 * weight (SRP × qty); individual allocations stay editable. An optional
 * affiliate % comes out of the DISTRIBUTOR's spread — the owner is still
 * owed full wholesale. Books one settlement with the whole money story.
 */
export function BulkSaleDialog({ products, holderId, holderName, onClose, onDone }) {
  const { t } = useTranslation();
  const inStock = useMemo(
    () => (products || []).filter((p) => (p.current_stock || 0) > 0),
    [products]
  );
  const [total, setTotal] = useState("");
  const [affiliatePct, setAffiliatePct] = useState("");
  const [recipient, setRecipient] = useState("");
  const [mode, setMode] = useState("received"); // received | settled
  const [rows, setRows] = useState([]); // {product_id, qty, alloc, dirty}
  const [busy, setBusy] = useState(false);

  const addRow = () => {
    const used = new Set(rows.map((r) => r.product_id));
    const next = inStock.find((p) => !used.has(p.id));
    if (!next) return;
    autoSplit([...rows, { product_id: next.id, qty: 1, alloc: "", dirty: false }], total);
  };

  const prodOf = (id) => inStock.find((p) => p.id === id);

  // Proportional auto-split over non-dirty rows of whatever remains after
  // the manually-edited (dirty) rows take their share.
  const autoSplit = (nextRows, totalStr) => {
    const tot = Number(totalStr) || 0;
    const dirtySum = nextRows.filter((r) => r.dirty).reduce((s, r) => s + (Number(r.alloc) || 0), 0);
    const free = nextRows.filter((r) => !r.dirty);
    const remain = Math.max(0, tot - dirtySum);
    const weights = free.map((r) => {
      const p = prodOf(r.product_id);
      const srp = p?.suggested_retail_price != null ? Number(p.suggested_retail_price) : 1;
      return Math.max(0.01, srp) * (Number(r.qty) || 1);
    });
    const wSum = weights.reduce((s, w) => s + w, 0) || 1;
    let allocated = 0;
    const out = nextRows.map((r) => ({ ...r }));
    let fi = 0;
    for (let i = 0; i < out.length; i++) {
      if (out[i].dirty) continue;
      const isLast = fi === free.length - 1;
      const share = isLast ? Math.max(0, remain - allocated) : Math.round((remain * weights[fi] / wSum) * 100) / 100;
      allocated += share;
      out[i].alloc = share ? String(share.toFixed(2)) : "0";
      fi++;
    }
    setRows(out);
  };

  const setRow = (idx, patch, reSplit = false) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    if (reSplit) autoSplit(next, total);
    else setRows(next);
  };

  const removeRow = (idx) => autoSplit(rows.filter((_, i) => i !== idx), total);

  const onTotalChange = (v) => {
    setTotal(v);
    autoSplitWith(v);
  };
  const autoSplitWith = (v) => autoSplit(rows, v);

  const grossN = Number(total) || 0;
  const pctN = Math.min(100, Math.max(0, Number(affiliatePct) || 0));
  const affiliateCut = Math.round(grossN * pctN) / 100;
  const netCash = Math.round((grossN - affiliateCut) * 100) / 100;
  const allocSum = rows.reduce((s, r) => s + (Number(r.alloc) || 0), 0);
  const allocDiff = Math.round((grossN - allocSum) * 100) / 100;
  const basisTotal = rows.reduce((s, r) => {
    const p = prodOf(r.product_id);
    const ws = p?.wholesale_price != null ? Number(p.wholesale_price) : 0;
    return s + ws * (Number(r.qty) || 0);
  }, 0);
  const credit = Math.max(0, Math.round((netCash - basisTotal) * 100) / 100);

  const submit = async () => {
    if (!grossN || rows.length === 0) {
      toast.error(t("bulk_sale.need_total_and_items"));
      return;
    }
    for (const r of rows) {
      const p = prodOf(r.product_id);
      if ((Number(r.qty) || 0) > (p?.current_stock || 0)) {
        toast.error(t("assign.only_n_on_hand", { n: p?.current_stock || 0 }));
        return;
      }
    }
    if (Math.abs(allocDiff) > 0.05) {
      toast.error(t("bulk_sale.alloc_mismatch", { diff: fmt$(Math.abs(allocDiff)) }));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/distributors/${holderId}/bulk-sale`, {
        total_sale: grossN,
        affiliate_pct: pctN,
        settlement_mode: mode,
        recipient_name: recipient.trim() || undefined,
        items: rows.map((r) => ({
          product_id: r.product_id,
          quantity: Number(r.qty) || 1,
          allocated_amount: Number(r.alloc) || 0,
        })),
      });
      toast.success(t("bulk_sale.booked", { ref: data.reference, credit: fmt$(data.distributor_credit) }));
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-sale-dialog"
      >
        <h2 className="text-xl font-semibold mb-1">{t("bulk_sale.title")}</h2>
        <p className="text-sm text-zinc-600 mb-4">{t("bulk_sale.subtitle", { name: holderName })}</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("bulk_sale.total_label")}</label>
            <BrutalInput type="number" min="0" step="0.01" value={total}
              onChange={(e) => onTotalChange(e.target.value)}
              placeholder="0.00" autoFocus data-testid="bulk-sale-total-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("bulk_sale.affiliate_label")}</label>
            <BrutalInput type="number" min="0" max="100" step="0.5" value={affiliatePct}
              onChange={(e) => setAffiliatePct(e.target.value)}
              placeholder="0" data-testid="bulk-sale-affiliate-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("bulk_sale.recipient_label")}</label>
            <BrutalInput value={recipient} onChange={(e) => setRecipient(e.target.value)}
              placeholder={t("bulk_sale.recipient_ph")} data-testid="bulk-sale-recipient-input" />
          </div>
        </div>

        <div className="space-y-2 mb-2">
          {rows.map((r, idx) => {
            const p = prodOf(r.product_id);
            const used = new Set(rows.map((x) => x.product_id));
            return (
              <div key={r.product_id} className="flex flex-wrap items-center gap-2 border border-zinc-200 rounded-lg p-2" data-testid={`bulk-sale-row-${idx}`}>
                <select
                  className="flex-1 min-w-[160px] border border-zinc-300 rounded-md px-2 h-9 text-sm"
                  value={r.product_id}
                  onChange={(e) => setRow(idx, { product_id: e.target.value }, true)}
                  data-testid={`bulk-sale-product-select-${idx}`}
                >
                  {inStock.filter((x) => x.id === r.product_id || !used.has(x.id)).map((x) => (
                    <option key={x.id} value={x.id}>
                      {`${x.name} (${x.current_stock} ${t("bulk_sale.on_hand")})`}
                    </option>
                  ))}
                </select>
                <BrutalInput type="number" min="1" max={p?.current_stock || 1} value={r.qty}
                  onChange={(e) => setRow(idx, { qty: e.target.value }, true)}
                  className="w-20" data-testid={`bulk-sale-qty-${idx}`} />
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400">$</span>
                  <BrutalInput type="number" min="0" step="0.01" value={r.alloc}
                    onChange={(e) => setRow(idx, { alloc: e.target.value, dirty: true })}
                    className="w-28 pl-5" data-testid={`bulk-sale-alloc-${idx}`} />
                </div>
                <button type="button" onClick={() => removeRow(idx)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`bulk-sale-remove-${idx}`}>
                  <Trash size={16} />
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-3 mb-4">
          <BrutalButton type="button" variant="outline" size="sm" onClick={addRow}
            disabled={rows.length >= inStock.length} data-testid="bulk-sale-add-item">
            <Plus size={14} weight="bold" /> {t("bulk_sale.add_item")}
          </BrutalButton>
          {rows.some((r) => r.dirty) && (
            <button type="button" className="text-xs underline text-zinc-500"
              onClick={() => autoSplit(rows.map((r) => ({ ...r, dirty: false })), total)}
              data-testid="bulk-sale-resplit">
              {t("bulk_sale.resplit")}
            </button>
          )}
          {Math.abs(allocDiff) > 0.05 && grossN > 0 && (
            <span className="text-xs font-semibold text-red-600" data-testid="bulk-sale-alloc-diff">
              {allocDiff > 0 ? t("bulk_sale.under_by", { amt: fmt$(allocDiff) }) : t("bulk_sale.over_by", { amt: fmt$(-allocDiff) })}
            </span>
          )}
        </div>

        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-sm space-y-1 mb-4" data-testid="bulk-sale-summary">
          <div className="flex justify-between"><span className="text-zinc-500">{t("bulk_sale.gross")}</span><span className="font-semibold tabular-nums">{fmt$(grossN)}</span></div>
          {pctN > 0 && (
            <div className="flex justify-between"><span className="text-zinc-500">{t("bulk_sale.affiliate_cut", { pct: pctN })}</span><span className="font-semibold tabular-nums text-red-600">−{fmt$(affiliateCut)}</span></div>
          )}
          <div className="flex justify-between"><span className="text-zinc-500">{t("bulk_sale.net_cash")}</span><span className="font-semibold tabular-nums">{fmt$(netCash)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">{t("bulk_sale.wholesale_owed")}</span><span className="font-semibold tabular-nums">{fmt$(basisTotal)}</span></div>
          <div className="flex justify-between border-t border-zinc-200 pt-1">
            <span className="font-semibold">{t("bulk_sale.balance_owed", { name: holderName })}</span>
            <span className="font-bold tabular-nums text-emerald-700" data-testid="bulk-sale-credit">{fmt$(credit)}</span>
          </div>
        </div>

        <div className="space-y-2 mb-5">
          {[
            ["received", t("bulk_sale.mode_received")],
            ["settled", t("bulk_sale.mode_settled")],
          ].map(([val, label]) => (
            <label key={val} className="flex items-start gap-2 cursor-pointer select-none">
              <input type="radio" name="bulk-mode" checked={mode === val}
                onChange={() => setMode(val)} className="mt-0.5 accent-[#c9a24a]"
                data-testid={`bulk-sale-mode-${val}`} />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>

        <div className="flex gap-3">
          <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="button" className="flex-1" disabled={busy || rows.length === 0}
            onClick={submit} data-testid="bulk-sale-submit">
            {busy ? t("assign.sending") : t("bulk_sale.submit")}
          </BrutalButton>
        </div>
      </div>
    </div>
  );
}
